import React, { useCallback, useEffect, useState } from "react";
import * as styles from "./SiteSearchResults.module.css";
import {
  InstantSearch,
  Configure,
  InfiniteHits,
  connectStateResults,
  RefinementList,
  Panel,
} from "react-instantsearch-dom";
import { searchIndex, searchClient } from "../../../lib/algolia";
import DebouncedSearchBox from "../../elements/SearchInput/DebouncedSearchBox";
import { ComponentProps } from "../../../types/index";
import { SectionWrapper } from "../../containers/SectionWrapper";
import Modal from "react-modal";
import ReactPlayer from "react-player/lazy";
import CloseButton from "../../elements/CloseButton/CloseButton";
import { SearchState } from "react-instantsearch-core";
import DebouncedAdvancedSearchBox from "./DebouncedAdvancedSearchBox";
import { useBreakpoint } from "gatsby-plugin-breakpoints";
import { CustomRichTextElement } from "../../elements/CustomRichTextElement/CustomRichTextElement";
import { formatImages } from "../../../lib/images";
import { KontentImageElement } from "../../elements/KontentImageElement/KontentImageElement";

interface Props extends ComponentProps {
  searchTerm?: SearchState;
  onSearchTermChange?: (arg: string) => void;
  createURL?: (arg: string) => string;
  includeContentCategory?: boolean;
  contentCategoryTitle?: string;
  includePageType?: boolean;
  pageTypeTitle?: string;
  includeRelevancyTags?: boolean;
  relevancyTagsTitle?: string;
}

interface AlgoliaMatch {
  value: string;
  matchLevel: string;
  matchedWords: string[];
}
interface SearchResult {
  url: string;
  title: string;
  objectId: string;
  description: string;
  contentCategory: string;
  content: any; // page content components
  _hightlightResult: {
    url: AlgoliaMatch;
    title: AlgoliaMatch;
    description: AlgoliaMatch;
    contentCategory: string;
  };
  image?: {
    system: {
      type: "media___image";
    };
    elements: {
      alt_text: {
        value: string;
      };
      image?: {
        value?: {
          description: string;
          width: number;
          height: number;
          url: string;
          name: string;
          size: number;
        }[];
      };
      small_image?: {
        value?: {
          description: string;
          width: number;
          height: number;
          url: string;
          name: string;
          size: number;
        }[];
      };
      caption: {
        value?: string;
      };
    };
  };
}

interface HitItem {
  hit: SearchResult;
}

export const SiteSearchResults: React.FC<Props> = ({
  includeContentCategory,
  includePageType,
  includeRelevancyTags,
  contentCategoryTitle,
  pageTypeTitle,
  relevancyTagsTitle,
  ...props
}) => {
  if (typeof document === "undefined") return null;

  const breakpoints = useBreakpoint();

  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [useAdvancedSearch, setUseAdvancedSearch] = useState(false);
  const handlePopState = useCallback((event) => {
    setVideoModalOpen(false);
  }, []);

  const handleAdvancedClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setUseAdvancedSearch(!useAdvancedSearch);
  };

  const handleVideoButtonClick = (videoUrl: string) => {
    window.history.pushState({}, "", "#video");
    setVideoModalOpen(true);
    setVideoUrl(videoUrl);
  };

  useEffect(() => {
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  //@ts-ignore
  const ResultWrapper = connectStateResults(({ searchResults, children }) => {
    return searchResults && searchResults.nbHits !== 0 ? (
      <div className={styles.results}>
        <div className={styles.resultsInfo}>
          {searchResults.nbHits} results{" "}
          {searchResults.query ? `for ${searchResults.query}` : ""}
        </div>
        {children}
      </div>
    ) : (
      <div className={styles.resultsInfo}>
        No results found for {searchResults?.query}{" "}
      </div>
    );
  });

  const Hit = (props: HitItem) => {
    const image = formatImages(props.hit?.image?.elements)?.largeImage;

    const renderImage = () => {
      if (image.item) {
        return (
          <div>
            <KontentImageElement
              image={image.item}
              alt={image.alt}
              height={125}
              width={125}
            />
          </div>
        );
      }

      if (props.hit.contentCategory === "Video") {
        return (
          <button
            className={styles.hitLink}
            type="button"
            onClick={() => handleVideoButtonClick(props.hit.url)}
          >
            <div>
              <ReactPlayer
                url={props.hit.url}
                light={true}
                height={125}
                width={125}
              />
            </div>
          </button>
        );
      }
    };

    return (
      <div className={styles.hit}>
        <div className={styles.hitMetadata}>
          <div className={styles.hitCategory}>
            {props.hit.contentCategory &&
              props.hit.contentCategory != "Uncategorized" && (
                <span className={styles.contentCategory}>
                  {props.hit.contentCategory}
                </span>
              )}
          </div>
          {(props.hit.contentCategory === "Video" || image.item) &&
            renderImage()}
        </div>
        <HitContent {...props} />
      </div>
    );
  };

  const HitContent = (props: HitItem) => {
    if (props.hit.contentCategory === "Video") {
      return (
        <div className={styles.hitContent}>
          <button
            className={styles.hitLink}
            type="button"
            onClick={() => handleVideoButtonClick(props.hit.url)}
          >
            <h3>{props.hit.title}</h3>
            <p>{props.hit.description}</p>
          </button>
        </div>
      );
    }

    return (
      <div className={styles.hitContent}>
        <a className={styles.hitLink} href={props.hit.url}>
          <h3>{props.hit.title}</h3>
          {props.hit.description?.startsWith("<p>") ? (
            <CustomRichTextElement content={{ value: props.hit.description }} />
          ) : (
            <p>{props.hit.description}</p>
          )}
        </a>
      </div>
    );
  };

  //@ts-ignore
  const sortItems = (items) => {
    return items.sort((a: any, b: any) => {
      if (a.label > b.label) return 1;
      if (a.label < b.label) return -1;
      return 0;
    });
  };

  return (
    <SectionWrapper {...props}>
      <Modal
        isOpen={videoModalOpen}
        preventScroll={true}
        appElement={document?.body}
        shouldCloseOnEsc={true}
        onRequestClose={() => setVideoModalOpen(false)}
        style={{
          overlay: {
            zIndex: 1000,
          },
          content: {
            border: "none",
            borderRadius: 0,
            padding: "2rem",
          },
        }}
      >
        <div className={styles.modalCloseButton}>
          <CloseButton
            visualStyle="dark"
            onClick={() => setVideoModalOpen(false)}
            size={24}
          />
        </div>
        <ReactPlayer url={videoUrl} height="100%" width="100%" controls />
      </Modal>
      <div className={styles.siteSearchResults}>
        <InstantSearch
          indexName={searchIndex.siteSearch}
          searchClient={searchClient}
          searchState={props.searchTerm}
          onSearchStateChange={props.onSearchTermChange}
          createURL={props.createURL}
        >
          <Configure hitsPerPage={20} />
          {!useAdvancedSearch ? (
            <div className={styles.searchContainer}>
              <DebouncedSearchBox
                placeHolder="Search the site..."
                delay={200}
              />
              <div className={styles.advancedBtnWrap}>
                <button
                  onClick={handleAdvancedClick}
                  className={styles.advancedBtn}
                  type="button"
                >
                  <span>+ </span>
                  <span>ADVANCED SEARCH</span>
                </button>
              </div>
            </div>
          ) : (
            <DebouncedAdvancedSearchBox
              placeHolder="Search the site..."
              delay={200}
              handleAdvancedClick={handleAdvancedClick}
              includeContentCategory={includeContentCategory}
              contentCategoryTitle={contentCategoryTitle}
              includePageType={includePageType}
              pageTypeTitle={pageTypeTitle}
              includeRelevancyTags={includeRelevancyTags}
              relevancyTagsTitle={relevancyTagsTitle}
            />
          )}
          <div className={styles.filterResultsContainer}>
            {breakpoints.l && (
              <div className={styles.categoryRefinement}>
                {includeContentCategory && (
                  <Panel header={contentCategoryTitle || "Content Category"}>
                    <RefinementList
                      attribute="contentCategory"
                      limit={50}
                      transformItems={sortItems}
                    ></RefinementList>
                  </Panel>
                )}
                {includePageType && (
                  <Panel header={pageTypeTitle || "Page Type"}>
                    <RefinementList
                      attribute="pageType"
                      limit={50}
                      transformItems={sortItems}
                    ></RefinementList>
                  </Panel>
                )}
                {includeRelevancyTags && (
                  <Panel header={relevancyTagsTitle || "Relevancy Tags"}>
                    <RefinementList
                      attribute="relevancyTags"
                      limit={50}
                      transformItems={sortItems}
                    ></RefinementList>
                  </Panel>
                )}
              </div>
            )}
            <ResultWrapper>
              <InfiniteHits
                hitComponent={Hit}
                translations={{ loadMore: "Show More" }}
              />
            </ResultWrapper>
          </div>
        </InstantSearch>
      </div>
    </SectionWrapper>
  );
};
